import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, Play, Users, Calendar, ArrowRight, Clock, TrendingUp, Activity, CheckCircle, Star, Award, CalendarCheck, Shield, Zap, BarChart, Heart, Monitor } from 'lucide-react';
import { useCreators } from '@/hooks/useCreators';
import { useHybridVideos } from '@/hooks/useHybridVideos';
import { useAuth } from '@/hooks/useAuth';
import VideoCardWrapper from '@/components/layout/VideoCardWrapper';
import Loader from '@/components/ui/Loader';
import Navbar from '@/components/layout/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { motion, AnimatePresence } from 'framer-motion';
import YouTubeQuotaInfo from '@/components/dashboard/YouTubeQuotaInfo';
import { PlaylistProvider } from '@/contexts/PlaylistContext';
import { 
  containerVariants, 
  itemVariants, 
  cardVariants, 
  buttonVariants, 
  fadeIn, 
  drawPathVariants,
  statsVariants,
  fadeInUp,
  slideInRight,
  slideInLeft,
  staggerContainer,
  glowPulse
} from '@/lib/animations';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

const IndexContent = () => {
  const { user } = useAuth();
  const { videos, loading: videosLoading } = useHybridVideos();
  const { subscribedCreators, loading: creatorsLoading } = useCreators();
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [date, setDate] = useState("");
  const [open, setOpen] = useState(false);
  
  const formattedDate = new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
  
  const formattedTime = lastUpdated.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true
  });
  
  const hasSubscribedCreators = subscribedCreators.length > 0;
  
  const handleRefresh = () => {
    setLastUpdated(new Date());
  };

  const handleSubmitDemo = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!name || !email || !company || !date) {
      toast({
        title: "Formulaire incomplet",
        description: "Veuillez remplir tous les champs du formulaire",
        variant: "destructive",
      });
      return;
    }
    
    // Success message
    toast({
      title: "Démo réservée !",
      description: `Votre démo est confirmée pour le ${date}. Un email de confirmation a été envoyé à ${email}.`,
      variant: "default",
    });
    
    // Reset form
    setName("");
    setEmail("");
    setCompany("");
    setDate("");
    setOpen(false);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-black overflow-hidden">
        <Navbar />
        
        <main className="relative pt-16">
          {/* Hero Section - Enhanced with more abstract design elements */}
          <section className="relative pt-20 pb-32 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 opacity-80 dark:opacity-40 z-0"></div>
            
            {/* Abstract design elements */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1 }}
              className="absolute inset-0 z-0 overflow-hidden"
            >
              <div className="absolute top-[-5%] right-[-10%] w-[600px] h-[600px] rounded-full bg-gradient-to-br from-youtube-red/10 to-red-500/5 blur-3xl"></div>
              <div className="absolute top-[20%] left-[-10%] w-[500px] h-[500px] rounded-full bg-gradient-to-tr from-blue-500/10 to-purple-500/5 blur-3xl"></div>
              <div className="absolute bottom-[-10%] right-[20%] w-[400px] h-[400px] rounded-full bg-gradient-to-bl from-amber-500/10 to-yellow-500/5 blur-3xl"></div>
              
              <svg className="absolute top-0 left-0 w-full h-full opacity-30 dark:opacity-10" viewBox="0 0 100 100" preserveAspectRatio="none">
                <motion.path
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{ duration: 2, ease: "easeInOut" }}
                  d="M0,0 L100,0 L100,100 L0,100 Z"
                  stroke="rgba(239, 68, 68, 0.2)"
                  strokeWidth="0.5"
                  fill="none"
                />
                <motion.path
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{ duration: 2, delay: 0.5, ease: "easeInOut" }}
                  d="M0,50 Q25,30 50,50 T100,50"
                  stroke="rgba(239, 68, 68, 0.3)"
                  strokeWidth="0.5"
                  fill="none"
                />
                <motion.path
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{ duration: 2, delay: 1, ease: "easeInOut" }}
                  d="M0,80 Q40,60 80,80 T160,80"
                  stroke="rgba(239, 68, 68, 0.2)"
                  strokeWidth="0.5"
                  fill="none"
                />
              </svg>
              
              <div className="absolute inset-0 bg-grid-gray-900/[0.02] dark:bg-grid-white/[0.05]"></div>
            </motion.div>
            
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-5xl mx-auto">
                <AnimatePresence>
                  <motion.div 
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    variants={containerVariants}
                    className="text-center mb-16"
                  >
                    <motion.div 
                      variants={itemVariants}
                      className="inline-block py-1.5 px-4 rounded-full bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/30 dark:to-red-800/20 text-youtube-red dark:text-red-300 text-sm font-medium mb-6 border border-red-100 dark:border-red-800/40 shadow-sm"
                    >
                      <motion.span
                        animate={{ 
                          opacity: [1, 0.7, 1],
                          scale: [1, 1.02, 1]
                        }}
                        transition={{ 
                          duration: 2,
                          repeat: Infinity,
                          repeatType: "reverse"
                        }}
                      >
                        La nouvelle façon de consommer YouTube
                      </motion.span>
                    </motion.div>
                    
                    <motion.h1 
                      variants={itemVariants}
                      className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight mb-8 tracking-tight text-balance"
                    >
                      <span className="bg-gradient-to-r from-youtube-red via-red-500 to-red-600 dark:via-red-400 dark:to-red-500 bg-clip-text text-transparent">
                        Suivez tous vos créateurs
                      </span>
                      <br />
                      <span className="bg-gradient-to-r from-gray-800 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                        en un seul endroit
                      </span>
                    </motion.h1>
                    
                    <motion.p 
                      variants={itemVariants}
                      className="text-gray-600 dark:text-gray-300 text-lg sm:text-xl mb-10 mx-auto max-w-2xl leading-relaxed"
                    >
                      PersonalTube révolutionne votre expérience de visionnage en centralisant et organisant le contenu 
                      de vos créateurs favoris de manière <span className="font-semibold">intuitive</span>, <span className="font-semibold">personnalisée</span> et <span className="text-youtube-red font-semibold">sans distractions</span>.
                    </motion.p>
                    
                    <motion.div 
                      variants={itemVariants}
                      className="flex flex-wrap items-center gap-4 justify-center"
                    >
                      <motion.div
                        variants={buttonVariants}
                        whileHover="hover"
                        whileTap="tap"
                        className="relative"
                      >
                        <motion.div 
                          className="absolute -inset-0.5 rounded-full opacity-75"
                          animate={{
                            boxShadow: ['0 0 0 0 rgba(239, 68, 68, 0)', '0 0 0 10px rgba(239, 68, 68, 0)'],
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                          }}
                        />
                        <Link to="/auth?tab=signup" className="relative group overflow-hidden rounded-full bg-youtube-red p-0.5 text-white shadow-lg shadow-red-500/20 hover:shadow-red-500/40 transition-all duration-300">
                          <span className="relative flex items-center gap-2 rounded-full bg-youtube-red px-7 py-4 font-medium transition-all duration-300 ease-in-out group-hover:bg-opacity-90">
                            <Play className="w-5 h-5" />
                            Commencer gratuitement
                          </span>
                        </Link>
                      </motion.div>
                      
                      <motion.div
                        variants={buttonVariants}
                        whileHover="hover"
                        whileTap="tap"
                      >
                        <Link to="/auth?tab=login" className="relative flex items-center justify-center rounded-full border-2 border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm px-7 py-4 text-base font-medium text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700/90 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5">
                          J'ai déjà un compte
                        </Link>
                      </motion.div>
                    </motion.div>
                  </motion.div>
                </AnimatePresence>
                
                {/* Visual Element - Abstract UI Mockup */}
                <motion.div
                  initial={{ opacity: 0, y: 100 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.8 }}
                  className="relative max-w-4xl mx-auto px-4 -mb-32 z-20"
                >
                  <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl overflow-hidden shadow-2xl border border-gray-700/50">
                    <div className="absolute inset-0 bg-grid-white/5 [mask-image:linear-gradient(to_bottom,white,transparent)]"></div>
                    <div className="absolute inset-0 bg-gradient-to-br from-youtube-red/10 to-transparent"></div>
                    
                    <div className="relative pt-4 pb-10 px-4 md:p-8">
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-red-500"></div>
                          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                          <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        </div>
                        <div className="h-6 w-40 bg-gray-800 rounded-md"></div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                        <div className="col-span-3 hidden md:block">
                          <div className="space-y-3">
                            <div className="h-10 bg-gray-800/80 rounded-lg flex items-center px-3">
                              <div className="w-6 h-6 rounded-full bg-youtube-red/60 mr-2"></div>
                              <div className="h-4 bg-gray-700 rounded w-2/3"></div>
                            </div>
                            {[1, 2, 3, 4, 5].map((item) => (
                              <div key={item} className="h-10 bg-gray-800/40 rounded-lg flex items-center px-3">
                                <div className="w-6 h-6 rounded-full bg-gray-700 mr-2"></div>
                                <div className="h-4 bg-gray-700/70 rounded w-3/4"></div>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <div className="col-span-9">
                          <div className="space-y-4">
                            <div className="h-10 w-full md:w-1/2 bg-gray-800 rounded-full flex items-center px-4">
                              <div className="w-4 h-4 rounded-full bg-gray-600 mr-2"></div>
                              <div className="h-4 bg-gray-700 rounded-full w-full"></div>
                            </div>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                              {[1, 2, 3, 4, 5, 6].map((item) => (
                                <div key={item} className="bg-gray-800/60 rounded-lg overflow-hidden">
                                  <div className="aspect-video bg-gradient-to-br from-gray-700 to-gray-800 relative">
                                    <div className="absolute inset-0 flex items-center justify-center">
                                      <div className="w-10 h-10 rounded-full bg-youtube-red/30 flex items-center justify-center">
                                        <Play className="w-4 h-4 text-white" />
                                      </div>
                                    </div>
                                    <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-1 rounded">
                                      12:34
                                    </div>
                                  </div>
                                  <div className="p-3">
                                    <div className="flex items-start gap-2">
                                      <div className="w-8 h-8 rounded-full bg-gray-700 flex-shrink-0"></div>
                                      <div className="space-y-1 flex-1">
                                        <div className="h-4 bg-gray-700 rounded w-full"></div>
                                        <div className="h-3 bg-gray-700/50 rounded w-2/3"></div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </section>
          
          {/* Key Benefits Section */}
          <section className="py-32 bg-gradient-to-b from-white to-gray-50 dark:from-black dark:to-gray-900 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-white dark:to-black z-10"></div>
            
            <div className="container mx-auto px-4 relative z-20 pt-20">
              <motion.div 
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={containerVariants}
                className="max-w-3xl mx-auto text-center mb-16"
              >
                <motion.span 
                  variants={itemVariants}
                  className="inline-block py-1.5 px-4 rounded-full bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/20 text-blue-600 dark:text-blue-300 text-sm font-medium mb-6 border border-blue-100 dark:border-blue-800/40 shadow-sm"
                >
                  Avantages
                </motion.span>
                <motion.h2 
                  variants={itemVariants}
                  className="text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300 bg-clip-text text-transparent tracking-tight"
                >
                  Reprenez le contrôle de votre expérience YouTube
                </motion.h2>
                <motion.p 
                  variants={itemVariants}
                  className="text-gray-600 dark:text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed"
                >
                  PersonalTube a été conçu pour vous offrir une expérience sur mesure, centrée sur vos créateurs préférés.
                </motion.p>
              </motion.div>
              
              {/* Key Benefits with Visual Indicators */}
              <motion.div 
                initial="hidden"
                animate="visible"
                variants={staggerContainer}
                className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16 max-w-6xl mx-auto"
              >
                <motion.div 
                  variants={fadeInUp}
                  whileHover={{ y: -8, transition: { duration: 0.3 } }}
                  className="bg-white dark:bg-gray-800/60 rounded-2xl shadow-xl p-8 text-center transform transition-all duration-300 border border-gray-100 dark:border-gray-700/50 backdrop-blur-sm"
                >
                  <motion.div 
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ 
                      scale: 1, 
                      opacity: 1,
                      transition: { 
                        delay: 0.3,
                        duration: 0.5,
                        type: "spring", 
                        stiffness: 260, 
                        damping: 20 
                      }
                    }}
                    className="relative w-20 h-20 bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/10 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-inner overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-grid-red-600/[0.05]"></div>
                    <Award className="w-10 h-10 text-youtube-red" />
                  </motion.div>
                  <motion.h3 
                    className="text-xl font-bold mb-3"
                  >
                    Qualité sur mesure
                  </motion.h3>
                  <motion.p 
                    className="text-gray-600 dark:text-gray-400"
                  >
                    Découvrez uniquement le contenu qui compte pour vous, sans distractions ni recommandations non pertinentes.
                  </motion.p>
                </motion.div>
                
                <motion.div 
                  variants={fadeInUp}
                  custom={1}
                  whileHover={{ y: -8, transition: { duration: 0.3 } }}
                  className="bg-white dark:bg-gray-800/60 rounded-2xl shadow-xl p-8 text-center transform transition-all duration-300 border border-gray-100 dark:border-gray-700/50 backdrop-blur-sm"
                >
                  <motion.div 
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ 
                      scale: 1, 
                      opacity: 1,
                      transition: { 
                        delay: 0.4,
                        duration: 0.5,
                        type: "spring", 
                        stiffness: 260, 
                        damping: 20 
                      }
                    }}
                    className="relative w-20 h-20 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/10 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-inner overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-grid-blue-600/[0.05]"></div>
                    <TrendingUp className="w-10 h-10 text-blue-500" />
                  </motion.div>
                  <motion.h3 
                    className="text-xl font-bold mb-3"
                  >
                    Productivité optimisée
                  </motion.h3>
                  <motion.p 
                    className="text-gray-600 dark:text-gray-400"
                  >
                    Retrouvez instantanément les vidéos de vos créateurs favoris sans perdre de temps à les rechercher.
                  </motion.p>
                </motion.div>
                
                <motion.div 
                  variants={fadeInUp}
                  custom={2}
                  whileHover={{ y: -8, transition: { duration: 0.3 } }}
                  className="bg-white dark:bg-gray-800/60 rounded-2xl shadow-xl p-8 text-center transform transition-all duration-300 border border-gray-100 dark:border-gray-700/50 backdrop-blur-sm"
                >
                  <motion.div 
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ 
                      scale: 1, 
                      opacity: 1,
                      transition: { 
                        delay: 0.5,
                        duration: 0.5,
                        type: "spring", 
                        stiffness: 260, 
                        damping: 20 
                      }
                    }}
                    className="relative w-20 h-20 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/10 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-inner overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-grid-purple-600/[0.05]"></div>
                    <Users className="w-10 h-10 text-purple-500" />
                  </motion.div>
                  <motion.h3 
                    className="text-xl font-bold mb-3"
                  >
                    Communauté active
                  </motion.h3>
                  <motion.p 
                    className="text-gray-600 dark:text-gray-400"
                  >
                    Connectez-vous avec des milliers d'utilisateurs qui partagent votre passion pour un contenu organisé.
                  </motion.p>
                </motion.div>
              </motion.div>
            </div>
          </section>
          
          {/* Features Grid Section */}
          <section className="py-24 bg-gray-50 dark:bg-gray-900 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-black opacity-80"></div>
            
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-3xl mx-auto text-center mb-16">
                <span className="inline-block py-1.5 px-4 rounded-full bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/20 text-green-600 dark:text-green-300 text-sm font-medium mb-6 border border-green-100 dark:border-green-800/40 shadow-sm">
                  Fonctionnalités
                </span>
                <h2 className="text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300 bg-clip-text text-transparent tracking-tight">
                  Des outils puissants, une expérience fluide
                </h2>
                <p className="text-gray-600 dark:text-gray-400 text-lg">
                  PersonalTube vous offre tout ce dont vous avez besoin pour suivre vos créateurs préférés.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {[
                  {
                    icon: <Calendar className="w-5 h-5" />,
                    title: "Organisation quotidienne",
                    description: "Consultez les nouvelles vidéos organisées par jour.",
                    color: "bg-red-500",
                    bg: "bg-red-50 dark:bg-red-900/20",
                    border: "border-red-100 dark:border-red-800/40",
                    delay: 0
                  },
                  {
                    icon: <Users className="w-5 h-5" />,
                    title: "Suivi de créateurs",
                    description: "Centralisez le contenu de vos créateurs préférés.",
                    color: "bg-blue-500",
                    bg: "bg-blue-50 dark:bg-blue-900/20",
                    border: "border-blue-100 dark:border-blue-800/40",
                    delay: 0.1
                  },
                  {
                    icon: <Play className="w-5 h-5" />,
                    title: "Flux personnalisé",
                    description: "Profitez d'un flux adapté à vos préférences.",
                    color: "bg-green-500",
                    bg: "bg-green-50 dark:bg-green-900/20",
                    border: "border-green-100 dark:border-green-800/40",
                    delay: 0.2
                  },
                  {
                    icon: <Shield className="w-5 h-5" />,
                    title: "Sans publicité",
                    description: "Concentrez-vous sur le contenu sans distractions.",
                    color: "bg-purple-500",
                    bg: "bg-purple-50 dark:bg-purple-900/20",
                    border: "border-purple-100 dark:border-purple-800/40",
                    delay: 0.3
                  },
                  {
                    icon: <Zap className="w-5 h-5" />,
                    title: "Performance optimisée",
                    description: "Interface fluide et chargement rapide des vidéos.",
                    color: "bg-amber-500",
                    bg: "bg-amber-50 dark:bg-amber-900/20",
                    border: "border-amber-100 dark:border-amber-800/40",
                    delay: 0.4
                  },
                  {
                    icon: <BarChart className="w-5 h-5" />,
                    title: "Statistiques de visionnage",
                    description: "Suivez vos habitudes et obtenez des insights.",
                    color: "bg-teal-500",
                    bg: "bg-teal-50 dark:bg-teal-900/20",
                    border: "border-teal-100 dark:border-teal-800/40",
                    delay: 0.5
                  }
                ].map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ delay: feature.delay, duration: 0.5 }}
                    className="bg-white dark:bg-gray-800/60 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700/50 overflow-hidden"
                  >
                    <div className="p-6">
                      <div className="flex items-center mb-4">
                        <div className={`w-10 h-10 ${feature.bg} rounded-lg flex items-center justify-center mr-3 ${feature.border}`}>
                          <div className={`${feature.color} text-white p-1.5 rounded-md`}>
                            {feature.icon}
                          </div>
                        </div>
                        <h3 className="text-lg font-medium">{feature.title}</h3>
                      </div>
                      <p className="text-gray-600 dark:text-gray-400 text-sm">
                        {feature.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
          
          {/* Testimonials Section */}
          <section className="py-24 bg-white dark:bg-black relative overflow-hidden">
            <div className="absolute inset-0 bg-grid-gray-900/[0.02] dark:bg-grid-white/[0.05]"></div>
            
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-3xl mx-auto text-center mb-16">
                <span className="inline-block py-1.5 px-4 rounded-full bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/20 text-purple-600 dark:text-purple-300 text-sm font-medium mb-6 border border-purple-100 dark:border-purple-800/40 shadow-sm">
                  Témoignages
                </span>
                <h2 className="text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300 bg-clip-text text-transparent tracking-tight">
                  Ce qu'en disent nos utilisateurs
                </h2>
                <p className="text-gray-600 dark:text-gray-400 text-lg">
                  Découvrez comment PersonalTube transforme l'expérience YouTube de nos utilisateurs.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                {[
                  {
                    name: "Sophie M.",
                    role: "Créatrice de contenu",
                    text: "PersonalTube a changé ma façon de consommer YouTube. Plus besoin de chercher mes vidéos préférées, tout est organisé parfaitement !",
                    delay: 0
                  },
                  {
                    name: "Thomas L.",
                    role: "Designer UX",
                    text: "L'interface est intuitive et agréable à utiliser. Je peux enfin suivre mes créateurs favoris sans les distractions habituelles.",
                    delay: 0.2
                  },
                  {
                    name: "Marie D.",
                    role: "Étudiante",
                    text: "J'utilise PersonalTube tous les jours pour mes cours en ligne. L'organisation par thème a révolutionné mes révisions !",
                    delay: 0.4
                  }
                ].map((testimonial, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: testimonial.delay, duration: 0.5 }}
                    className="glass-effect backdrop-blur-sm bg-white/60 dark:bg-gray-800/60 p-6 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700/30"
                  >
                    <div className="relative">
                      <span className="block text-5xl text-youtube-red/80 dark:text-youtube-red/60 absolute -top-4 -left-1 opacity-30">"</span>
                      <p className="text-gray-700 dark:text-gray-300 mb-6 relative z-10 text-sm sm:text-base">
                        {testimonial.text}
                      </p>
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800 overflow-hidden mr-3 flex items-center justify-center">
                          <span className="text-lg font-semibold text-gray-600 dark:text-gray-300">{testimonial.name.charAt(0)}</span>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white text-sm">{testimonial.name}</h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{testimonial.role}</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
          
          {/* Final CTA Section */}
          <section className="py-20 bg-gradient-to-br from-youtube-red to-red-600 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-pattern opacity-10"></div>
            
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.2 }}
              transition={{ duration: 1 }}
              className="absolute inset-0 overflow-hidden"
            >
              {/* Abstract elements */}
              <svg className="absolute top-0 left-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                <motion.path
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 0.3 }}
                  transition={{ duration: 3, ease: "easeInOut" }}
                  d="M0,25 Q25,50 50,25 T100,25"
                  stroke="rgba(255, 255, 255, 0.3)"
                  strokeWidth="0.5"
                  fill="none"
                />
                <motion.path
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 0.15 }}
                  transition={{ duration: 3, delay: 0.5, ease: "easeInOut" }}
                  d="M0,75 Q25,100 50,75 T100,75"
                  stroke="rgba(255, 255, 255, 0.2)"
                  strokeWidth="0.5"
                  fill="none"
                />
                <motion.path
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 0.1 }}
                  transition={{ duration: 2, delay: 1, ease: "easeInOut" }}
                  d="M0,50 C20,60 40,45 60,55 S80,70 100,60"
                  stroke="rgba(255, 255, 255, 0.25)"
                  strokeWidth="0.5"
                  fill="none"
                />
              </svg>
            </motion.div>
            
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-3xl mx-auto text-center">
                <motion.h2 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="text-3xl md:text-4xl font-bold mb-6"
                >
                  Prêt à transformer votre expérience YouTube ?
                </motion.h2>
                
                <motion.p 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  className="text-lg mb-10 text-white/90"
                >
                  Rejoignez des milliers d'utilisateurs satisfaits et commencez à profiter d'une expérience de visionnage optimisée et personnalisée.
                </motion.p>
                
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                  className="flex flex-wrap justify-center gap-4"
                >
                  <Link
                    to="/auth?tab=signup"
                    className="bg-white text-youtube-red rounded-full px-8 py-3.5 font-medium hover:bg-gray-100 transition-all shadow-lg transform hover:-translate-y-1 hover:shadow-xl"
                  >
                    Créer un compte gratuit
                  </Link>
                  
                  <button
                    onClick={() => setOpen(true)}
                    className="border-2 border-white/80 bg-transparent rounded-full px-8 py-3.5 font-medium hover:bg-white/10 transition-all"
                  >
                    Demander une démo
                  </button>
                </motion.div>
              </div>
            </div>
          </section>
        </main>
        
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Réserver une démo</DialogTitle>
              <DialogDescription>
                Complétez le formulaire ci-dessous pour réserver une démonstration personnalisée.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmitDemo}>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="name" className="text-right">
                    Nom
                  </Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="col-span-3"
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="email" className="text-right">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="col-span-3"
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="company" className="text-right">
                    Entreprise
                  </Label>
                  <Input
                    id="company"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    className="col-span-3"
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="date" className="text-right">
                    Date
                  </Label>
                  <Input
                    id="date"
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="col-span-3"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="submit">Réserver</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
  
  // Dashboard view for logged-in users
  return (
    <div className="min-h-screen bg-youtube-light dark:bg-youtube-dark">
      <Navbar />
      
      <main className="container mx-auto px-4 py-6">
        <div className="rounded-3xl bg-white dark:bg-gray-800 shadow-lg p-6 md:p-8 mb-8 animate-fade-in">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4">
            <div>
              <h1 className="text-2xl font-bold">Bienvenue, {user.user_metadata?.full_name || user.email}</h1>
              <p className="text-gray-500 dark:text-gray-400">{formattedDate} • Dernière mise à jour à {formattedTime}</p>
            </div>
            
            <div className="flex flex-wrap gap-3">
              <Button 
                size="sm"
                variant="outline" 
                onClick={handleRefresh} 
                className="flex items-center gap-2"
                disabled={videosLoading || creatorsLoading}
              >
                <RefreshCw className={`h-4 w-4 ${videosLoading || creatorsLoading ? 'animate-spin' : ''}`} />
                Rafraîchir
              </Button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Créateurs suivis</p>
                    <h2 className="text-2xl font-bold">{subscribedCreators.length}</h2>
                  </div>
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-full">
                    <Users className="h-5 w-5 text-blue-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Vidéos disponibles</p>
                    <h2 className="text-2xl font-bold">{videos.length}</h2>
                  </div>
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-full">
                    <Play className="h-5 w-5 text-youtube-red" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Playlists</p>
                    <h2 className="text-2xl font-bold">0</h2>
                  </div>
                  <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-full">
                    <CalendarCheck className="h-5 w-5 text-purple-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Favoris</p>
                    <h2 className="text-2xl font-bold">0</h2>
                  </div>
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-full">
                    <Heart className="h-5 w-5 text-amber-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <YouTubeQuotaInfo className="mb-8" />
        </div>
        
        <div className="mb-8 animate-smooth-fade">
          {hasSubscribedCreators ? (
            <>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Ajouts récents</h2>
                <Link to="/feed" className="text-youtube-red hover:text-red-600 text-sm font-medium flex items-center gap-1">
                  Voir tout <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
              
              {videosLoading ? (
                <div className="text-center py-12">
                  <Loader size="large" />
                  <p className="text-gray-500 mt-4">Chargement des vidéos...</p>
                </div>
              ) : videos.length > 0 ? (
                <div className="video-grid">
                  {videos.slice(0, 4).map((video) => (
                    <VideoCardWrapper key={video.id} {...video} />
                  ))}
                </div>
              ) : (
                <div className="text-center bg-white dark:bg-gray-800 shadow-sm rounded-lg border border-gray-200 dark:border-gray-700 p-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-red-50 dark:bg-red-900/20 rounded-full mb-4">
                    <Play className="w-8 h-8 text-youtube-red" />
                  </div>
                  <h3 className="text-lg font-medium mb-2">Aucune vidéo disponible</h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
                    Vos créateurs n'ont pas encore publié de nouvelles vidéos. Revenez plus tard ou abonnez-vous à d'autres créateurs.
                  </p>
                  <Link 
                    to="/creators" 
                    className="inline-flex items-center bg-youtube-red hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-all"
                  >
                    Explorer les créateurs
                  </Link>
                </div>
              )}
            </>
          ) : (
            <div className="text-center bg-white dark:bg-gray-800 shadow-sm rounded-lg border border-gray-200 dark:border-gray-700 p-8 animate-fade-in">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-50 dark:bg-blue-900/20 rounded-full mb-4">
                <Users className="w-8 h-8 text-blue-500" />
              </div>
              <h3 className="text-lg font-medium mb-2">Suivez vos créateurs préférés</h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
                Pour commencer à utiliser PersonalTube, abonnez-vous à vos créateurs favoris afin de retrouver tout leur contenu en un seul endroit.
              </p>
              <Link 
                to="/creators" 
                className="inline-flex items-center bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-all"
              >
                Explorer les créateurs
              </Link>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

const Index = () => {
  return (
    <PlaylistProvider>
      <IndexContent />
    </PlaylistProvider>
  );
};

export default Index;
